import {
	type BulkAclEntity,
	type ComparativeBenchmarksEditRequest,
	AclEntityMinInfoEntityTypeEnum,
	BenchmarksControllerApiFactory,
	EntityEditorControllerApiFactory,
	IntegrationReferenceControllerApiFactory,
	InvestmentComparativeBenchmarksControllerV4ApiFactory,
	InvestmentControllerV4ApiFactory,
} from "@eu.mdotm.api/sphere";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { type EventPayloadMap, eventBus } from "$root/event-bus";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { downloadContentDisposition, seconds } from "$root/utils";
import type { Abortable } from "@mdotm/utils/promise";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import debounce from "debounce";
import { reviewTickerToInstrumentsMap } from "../instruments/repository";

const referenceRepositoryBaseKeys = {
	referenceList: "referenceList",
	benchmarkList: "benchmarkList",
	projectedList: "referenceProjectedList",
};

const { newQuery, newAction, invalidateEntity, invalidateLists, invalidate, invalidateEntities } =
	repositoryHelperFactory("reference", {
		listKeys: Object.values(referenceRepositoryBaseKeys),
	});

const benchmarkApi = () => getApiGen(BenchmarksControllerApiFactory);
const investmentApi = () => getApiGen(InvestmentControllerV4ApiFactory);
const editorApi = () => getApiGen(EntityEditorControllerApiFactory);
const comparativeBenchmarkApi = () => getApiGen(InvestmentComparativeBenchmarksControllerV4ApiFactory);
const integrationApi = () => getApiGen(IntegrationReferenceControllerApiFactory);

export const referenceRepository = {
	invalidateEntity,
	invalidateLists,
	invalidate,
	benchmarkSummary: newQuery("benchmarkSummary", (uuid: string) =>
		axiosExtract(benchmarkApi().getBenchmarkSummary(uuid)),
	),

	benchmarkList: newQuery(referenceRepositoryBaseKeys.benchmarkList, () =>
		axiosExtract(benchmarkApi().getBenchmarkList()).then((list) => list.sort(builtInSortFnFor("creationTime", "desc"))),
	),

	referenceList: newQuery(referenceRepositoryBaseKeys.referenceList, () =>
		axiosExtract(investmentApi().getInvestmentReferenceList()).then((list) =>
			list.sort(builtInSortFnFor("creationTime", "desc")),
		),
	),

	benchmarkInstruments: newQuery("benchmarkInstruments", (benchmarkIdentifier: string) =>
		axiosExtract(editorApi().getBenchmarkInstruments(benchmarkIdentifier)).then((x) => x.composition),
	),

	benchmarkInstrumentsWithInstrumentMap: newQuery(
		"benchmarkInstrumentsToInstrumentMap",
		async (benchmarkIdentifier: string) => {
			const composition = await axiosExtract(editorApi().getBenchmarkInstruments(benchmarkIdentifier)).then(
				(x) => x.composition,
			);
			const userInstrumentsMap = await reviewTickerToInstrumentsMap(composition ?? []);
			return { composition: composition ?? [], userInstrumentsMap };
		},
	),

	isBenchmarkNameAvailable: newAction((name: string, opts?: Abortable) =>
		axiosExtract(benchmarkApi().isBenchmarkNameAvailable(name, opts)),
	),

	getBenchmarkACL: newQuery("benchmarkAcl", (uuid: string) => axiosExtract(benchmarkApi().getAclEditStarter2(uuid))),

	updateBenchmarkACL: newAction(async (uuid: string, acl: Array<RichAccessControl>) => {
		await benchmarkApi().setACL2(uuid, acl);
		invalidateEntity(uuid);
	}),

	/** @deprecated */
	deleteBenchmark: newAction(async (uuid: string) => {
		//TODO(19385): remove this method
		await benchmarkApi().deleteBenchmark(uuid);

		invalidateLists();
	}),

	renameBenchmark: newAction(async (uuid: string, name: string) => {
		await benchmarkApi().renameBenchmark(uuid, name);

		invalidateEntity(uuid);
	}),

	isReferenceNameAvailable: newAction((name: string, opts?: Abortable) =>
		axiosExtract(investmentApi().isInvestmentNameAvailable(name, opts)),
	),

	getReferenceACL: newQuery("referenceAcl", (uuid: string) => axiosExtract(investmentApi().getAclEditStarter1(uuid))),

	updateReferenceACL: newAction(async (uuid: string, acl: Array<RichAccessControl>) => {
		await investmentApi().setACL1(uuid, acl);
		invalidateEntity(uuid);
	}),

	duplicateReference: newAction(async (uuid: string, name: string) => {
		const duplicate = await axiosExtract(investmentApi().duplicateInvestment(uuid ?? "", name));

		invalidateLists();
		return duplicate;
	}),

	deleteReference: newAction(async (uuid: string) => {
		await investmentApi().deleteInvestment(uuid);

		invalidateLists();
	}),

	renameReference: newAction(async (uuid: string, name: string) => {
		await investmentApi().renameInvestment(uuid, name);

		invalidateEntity(uuid);
	}),

	comparativeBenchmarkList: newQuery("comparativeBenchmarkList", (uuid: string) =>
		axiosExtract(comparativeBenchmarkApi().getAvailableComparativeBenchmarks(uuid)),
	),

	setComparativeBenchmark: newAction(
		(uuid: string, comparativeBenchmarksEditRequest: ComparativeBenchmarksEditRequest) =>
			axiosExtract(comparativeBenchmarkApi().setComparativeBenchmarks(uuid, comparativeBenchmarksEditRequest)),
	),
	comparativeBenchmark: newQuery("comparativeBenchmark", (uuid: string) =>
		axiosExtract(comparativeBenchmarkApi().getComparativeBenchmarks(uuid)),
	),
	bulkReferenceShare: newAction(async (bulkAclEntities: Array<BulkAclEntity>) => {
		await axiosExtract(investmentApi().setBulkACL1(bulkAclEntities));
		invalidateEntities(bulkAclEntities.map((entity) => entity.entityUuid!));
	}),
	bulkBenchmarkShare: newAction(async (bulkAclEntities: Array<BulkAclEntity>) => {
		await axiosExtract(benchmarkApi().setBulkACL2(bulkAclEntities));
		invalidateEntities(bulkAclEntities.map((entity) => entity.entityUuid!));
	}),

	benchmarkBulkAclPreview: newQuery("benchmarkBulkAclPreview", (acl: Array<BulkAclEntity>) =>
		axiosExtract(benchmarkApi().getBulkACLCheck2(acl)),
	),
	referenceBulkAclPreview: newQuery("referenceBulkAclPreview", (acl: Array<BulkAclEntity>) =>
		axiosExtract(investmentApi().getBulkACLCheck1(acl)),
	),
	/** `persist=false` downloads the file now; `persist=true` builds it in the background and notifies the user. */
	downloadBulkTemplate: newAction(async (uuids: string[], persist: boolean) => {
		if (persist) {
			await integrationApi().exportReferencesBulkTemplate(uuids, true);
			return;
		}
		const response = await integrationApi().exportReferencesBulkTemplate(uuids, false, { responseType: "blob" });
		await downloadContentDisposition(response);
	}),
} as const;

// portfolioRepository.rename({ uuid: "akghriugho", name: "rtgherigo" });
// const {useSummaryQuery}=portfolioRepository;
// const {summary: {useQuery as useSummaryQuery}}=portfolioRepository;
// const {summary}=portfolioRepository;
// summary.useQuery({ uuid: "u4398ry49rh", enhanced: false });

eventBus.addListener(
	"investment-update",
	debounce((e: EventPayloadMap["investment-update"]) => {
		if (e.type === "REFERENCE_UPDATE") {
			invalidateEntity(e.uuid ?? "");
		}
	}, seconds(2)),
);

eventBus.addListener(
	"shared-entity",
	debounce((e: EventPayloadMap["shared-entity"]) => {
		if (
			e.sharedEntityType === AclEntityMinInfoEntityTypeEnum.Benchmark ||
			e.sharedEntityType === AclEntityMinInfoEntityTypeEnum.Investment
		) {
			invalidateEntity(e.sharedEntityUuid ?? "");
		}
	}, seconds(2)),
);

eventBus.addListener(
	"shared-entities",
	debounce((e: EventPayloadMap["shared-entities"]) => {
		const referenceEntities = e.investments?.filter(
			(x) => x.sharedEntityType === "REFERENCE" || x.sharedEntityType === "BENCHMARK",
		);
		if (referenceEntities?.length && referenceEntities?.length > 0) {
			invalidateEntities(referenceEntities.flatMap((x) => (x.sharedEntityUuid ? [x.sharedEntityUuid] : [])));
		}
	}, seconds(2)),
);

eventBus.addListener(
	"benchmark-update",
	debounce((e: EventPayloadMap["benchmark-update"]) => {
		invalidateEntity(e.uuid ?? "");
	}, seconds(2)),
);
